import { useState } from 'react'
import { ChevronsUpDown, Plus, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import type { Condition, Op } from '@/lib/expr'
import type { FieldOption, FieldType } from '../types'

/** A field a condition may reference. */
export interface ConditionField {
    key: string
    label: string
    type: FieldType | 'status' | 'form'
    options?: FieldOption[]
    /** Heading the picker files this field under (e.g. the form it belongs to). */
    group?: string
    /** Short name shown under `group`; `label` stays the full "Group · Name". */
    name?: string
}

const OP_LABEL: Record<Op, string> = {
    equals: 'is',
    not_equals: 'is not',
    includes: 'includes',
    in: 'is one of',
    not_empty: 'has an answer',
    empty: 'has no answer',
    gt: '>',
    gte: '≥',
    lt: '<',
    lte: '≤',
}

const TEXT_OPS: Op[] = ['equals', 'not_equals', 'includes', 'not_empty', 'empty']
const SELECT_OPS: Op[] = ['equals', 'not_equals', 'in', 'not_empty', 'empty']
const MULTI_OPS: Op[] = ['includes', 'not_empty', 'empty']
const NUM_OPS: Op[] = ['equals', 'not_equals', 'gt', 'gte', 'lt', 'lte', 'not_empty', 'empty']

export function opsFor(type: ConditionField['type']): Op[] {
    switch (type) {
        case 'single_select': case 'yes_no': case 'status': case 'form': return SELECT_OPS
        case 'multi_select': return MULTI_OPS
        case 'number': return NUM_OPS
        default: return TEXT_OPS
    }
}

type Leaf = Extract<Condition, { field: string }>
const isLeaf = (c: Condition): c is Leaf => 'field' in c
const emptyAll: Condition = { all: [] }

function valueOptions(f: ConditionField | undefined): FieldOption[] | undefined {
    if (!f) return undefined
    if (f.type === 'yes_no') return [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]
    return f.options
}

/** Searchable, grouped field chooser: a long list of questions is unusable as a plain dropdown. */
function FieldPicker({ value, fields, onChange }: { value: string; fields: ConditionField[]; onChange: (key: string) => void }) {
    const [open, setOpen] = useState(false)
    const current = fields.find((f) => f.key === value)
    const groups = new Map<string, ConditionField[]>()
    for (const f of fields) groups.set(f.group ?? '', [...(groups.get(f.group ?? '') ?? []), f])

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button type="button" variant="outline" role="combobox" aria-expanded={open} aria-label="Field"
                    className="w-64 justify-between font-normal">
                    <span className="truncate">{current ? (current.name ?? current.label) : <span className="text-muted-foreground">Choose a field…</span>}</span>
                    <ChevronsUpDown data-icon="inline-end" className="opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[28rem] max-w-[calc(100vw-2rem)] p-0">
                <Command>
                    <CommandInput placeholder="Search questions…" />
                    <CommandList className="max-h-72">
                        <CommandEmpty>No matching question.</CommandEmpty>
                        {[...groups.entries()].map(([group, list]) => (
                            <CommandGroup key={group} heading={group || undefined}>
                                {list.map((f) => (
                                    <CommandItem key={f.key} value={`${group} ${f.name ?? f.label} ${f.key}`} data-checked={f.key === value}
                                        onSelect={() => { onChange(f.key); setOpen(false) }}>
                                        <span className="truncate">{f.name ?? f.label}</span>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        ))}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}

function LeafEditor({ value, fields, onChange, onRemove }: {
    value: Leaf
    fields: ConditionField[]
    onChange: (c: Leaf) => void
    onRemove: () => void
}) {
    const field = fields.find((f) => f.key === value.field)
    const ops = opsFor(field?.type ?? 'text')
    const opts = valueOptions(field)
    const noValue = value.op === 'empty' || value.op === 'not_empty'

    const setField = (key: string) => {
        const f = fields.find((x) => x.key === key)
        const op = opsFor(f?.type ?? 'text').includes(value.op) ? value.op : opsFor(f?.type ?? 'text')[0]
        onChange({ field: key, op, value: op === 'in' ? [] : undefined })
    }
    const setOp = (op: Op) => onChange({ ...value, op, value: op === 'in' ? [] : noValueOp(op) ? undefined : typeof value.value === 'string' ? value.value : undefined })

    return (
        <div className="flex flex-wrap items-center gap-2">
            <FieldPicker value={value.field} fields={fields} onChange={setField} />
            <Select value={value.op} onValueChange={(v) => setOp(v as Op)}>
                <SelectTrigger className="w-36" aria-label="Operator"><SelectValue /></SelectTrigger>
                <SelectContent><SelectGroup>
                    {ops.map((o) => <SelectItem key={o} value={o}>{OP_LABEL[o]}</SelectItem>)}
                </SelectGroup></SelectContent>
            </Select>
            {!noValue && (value.op === 'in' ? (
                <ListValue value={Array.isArray(value.value) ? (value.value as string[]) : []} options={opts}
                    onChange={(v) => onChange({ ...value, value: v })} />
            ) : opts ? (
                <Select value={typeof value.value === 'string' ? value.value : undefined} onValueChange={(v) => onChange({ ...value, value: v })}>
                    <SelectTrigger className="w-48" aria-label="Value"><SelectValue placeholder="Choose…" /></SelectTrigger>
                    <SelectContent><SelectGroup>
                        {opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectGroup></SelectContent>
                </Select>
            ) : (
                <Input
                    className="w-48"
                    aria-label="Value"
                    type={field?.type === 'number' ? 'number' : 'text'}
                    value={value.value === undefined || value.value === null ? '' : String(value.value)}
                    onChange={(e) => onChange({ ...value, value: field?.type === 'number' && e.target.value !== '' ? Number(e.target.value) : e.target.value })}
                />
            ))}
            <Button variant="ghost" size="icon" onClick={onRemove} aria-label="Remove condition"><Trash2 /></Button>
        </div>
    )
}

const noValueOp = (op: Op) => op === 'empty' || op === 'not_empty'

/** Comma-separated values, or checkboxes-as-chips when the field has options. */
function ListValue({ value, options, onChange }: { value: string[]; options?: FieldOption[]; onChange: (v: string[]) => void }) {
    if (options) {
        return (
            <ToggleGroup type="multiple" variant="outline" size="sm" value={value} onValueChange={onChange} className="flex-wrap">
                {options.map((o) => <ToggleGroupItem key={o.value} value={o.value}>{o.label}</ToggleGroupItem>)}
            </ToggleGroup>
        )
    }
    return (
        <Input className="w-56" aria-label="Values" placeholder="a, b, c" value={value.join(', ')}
            onChange={(e) => onChange(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />
    )
}

interface Props {
    value: Condition | undefined
    onChange: (c: Condition | undefined) => void
    fields: ConditionField[]
    /** Called for the outermost group only: text shown when there are no conditions. */
    emptyText?: string
    depth?: number
}

/**
 * Visual editor for the shared condition language ({all|any|not|field/op/value}).
 * A group is "all of"/"any of" over leaf conditions and nested groups. The
 * outermost value may be undefined (no condition).
 */
export default function ConditionEditor({ value, onChange, fields, emptyText = 'Always', depth = 0 }: Props) {
    const group: Condition = value ?? emptyAll
    const mode: 'all' | 'any' = 'any' in group ? 'any' : 'all'
    const items: Condition[] = 'any' in group ? group.any : 'all' in group ? group.all : [group]
    // A bare leaf / not at the root is shown as a one-item "all" group.
    const isGroup = 'all' in group || 'any' in group

    const emit = (m: 'all' | 'any', list: Condition[]) => {
        if (depth === 0 && list.length === 0) return onChange(undefined)
        onChange(m === 'all' ? { all: list } : { any: list })
    }
    const list = isGroup ? items : [group]
    const setItem = (i: number, c: Condition) => emit(mode, list.map((x, j) => (j === i ? c : x)))
    const removeItem = (i: number) => emit(mode, list.filter((_, j) => j !== i))
    const firstField = fields[0]?.key ?? ''
    const addLeaf = () => emit(mode, [...list, { field: firstField, op: opsFor(fields[0]?.type ?? 'text')[0] } as Condition])
    const addGroup = () => emit(mode, [...list, { all: [] }])

    return (
        <div className={depth > 0 ? 'flex flex-col gap-2 rounded-lg border bg-muted/30 p-3' : 'flex flex-col gap-3'}>
            {list.length === 0 ? (
                <p className="text-sm text-muted-foreground">{emptyText}</p>
            ) : (
                <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Match</span>
                    <ToggleGroup type="single" variant="outline" size="sm" value={mode}
                        onValueChange={(v) => v && emit(v as 'all' | 'any', list)}>
                        <ToggleGroupItem value="all">all</ToggleGroupItem>
                        <ToggleGroupItem value="any">any</ToggleGroupItem>
                    </ToggleGroup>
                    <span className="text-muted-foreground">of:</span>
                </div>
            )}
            {list.map((c, i) => (
                <div key={i}>
                    {isLeaf(c) ? (
                        <LeafEditor value={c} fields={fields} onChange={(n) => setItem(i, n)} onRemove={() => removeItem(i)} />
                    ) : 'not' in c ? (
                        <div className="flex items-start gap-2">
                            <Badge variant="outline" className="mt-2">not</Badge>
                            <div className="flex-1">
                                <ConditionEditor value={c.not} fields={fields} depth={depth + 1}
                                    onChange={(n) => (n ? setItem(i, { not: n }) : removeItem(i))} />
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-start gap-2">
                            <div className="flex-1">
                                <ConditionEditor value={c} fields={fields} depth={depth + 1}
                                    onChange={(n) => (n ? setItem(i, n) : removeItem(i))} />
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => removeItem(i)} aria-label="Remove group"><Trash2 /></Button>
                        </div>
                    )}
                </div>
            ))}
            <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={addLeaf} disabled={fields.length === 0}>
                    <Plus data-icon="inline-start" /> Condition
                </Button>
                {depth < 3 && (
                    <Button type="button" variant="ghost" size="sm" onClick={addGroup}>
                        <Plus data-icon="inline-start" /> Group
                    </Button>
                )}
            </div>
        </div>
    )
}

/** One-line human summary of a condition, for lists and chips. */
export function describeCondition(c: Condition | undefined, fields: ConditionField[]): string {
    if (!c) return 'Always'
    const label = (k: string) => fields.find((f) => f.key === k)?.label ?? k
    const valText = (f: Leaf) => {
        const opts = valueOptions(fields.find((x) => x.key === f.field))
        const one = (v: unknown) => opts?.find((o) => o.value === String(v))?.label ?? String(v)
        return Array.isArray(f.value) ? f.value.map(one).join(', ') : one(f.value)
    }
    const walk = (n: Condition, top: boolean): string => {
        if (isLeaf(n)) {
            return noValueOp(n.op) ? `${label(n.field)} ${OP_LABEL[n.op]}` : `${label(n.field)} ${OP_LABEL[n.op]} ${valText(n)}`
        }
        if ('not' in n) return `not (${walk(n.not, false)})`
        const parts = ('all' in n ? n.all : n.any).map((x) => walk(x, false))
        if (parts.length === 0) return 'Always'
        const joined = parts.join('all' in n ? ' and ' : ' or ')
        return top || parts.length === 1 ? joined : `(${joined})`
    }
    return walk(c, true)
}
